import type { UserWithUserProfile } from '@/common/domain-model';
import { clientStore } from '@/lib/client';
import { setCrashUser } from '@/lib/sentry';
import { makeAutoObservable } from 'mobx';

export class AuthStore {
  user: UserWithUserProfile | null = null;
  isLoading = true;

  constructor() {
    makeAutoObservable(this);
    this.initializeFromLocalStorage();
  }

  private initializeFromLocalStorage = async () => {
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken && accessToken !== 'undefined') {
      await this.fetchUser();
    } else {
      this.isLoading = false;
    }
  };

  private fetchUser = async () => {
    try {
      this.isLoading = true;
      const response = await clientStore.treatyClient.api.users.me.get();

      if (response.error) {
        throw new Error(response.error.value.message);
      }
      if (response.data && response.status === 200) {
        this.user = response.data.data;
        setCrashUser(this.user?.id);
      }
    } finally {
      this.isLoading = false;
    }
  };

  refetchUser = async () => {
    await clientStore.apolloClient.clearStore();
    await clientStore.apolloClient.resetStore();

    const accessToken = localStorage.getItem('accessToken');
    if (accessToken && accessToken !== 'undefined') {
      console.log('refetching user', this);
      await this.fetchUser();
    }
  };

  sendLoginCode = async (email: string) => {
    const response =
      await clientStore.treatyClient.api.auth.send_login_code.post({
        email,
      });
    if (response.error) {
      throw new Error(response.error.value.message);
    }
  };

  sendRegisterCode = async (
    email: string,
    name: string,
    referralCode: string,
  ) => {
    const response =
      await clientStore.treatyClient.api.auth.send_register_code.post({
        email,
        metadata: {
          name,
        },
        referralCode,
      });
    if (response.error) {
      throw new Error(response.error.value.message);
    }
  };

  verifyLoginCode = async (email: string, code: string) => {
    const response = await clientStore.treatyClient.api.auth.verify_login.post({
      email,
      code,
    });
    if (response.error) {
      throw new Error(response.error.value.message);
    }

    if (response.data && response.status === 200) {
      localStorage.setItem('accessToken', response.data.data.accessToken);
      localStorage.setItem('refreshToken', response.data.data.refreshToken);
      await this.fetchUser();
    }
  };

  verifyRegisterCode = async (email: string, code: string) => {
    const response =
      await clientStore.treatyClient.api.auth.verify_register.post({
        email,
        code,
      });
    if (response.error) {
      throw new Error(response.error.value.message);
    }
    if (response.data && response.status === 200) {
      localStorage.setItem('accessToken', response.data.data.accessToken);
      localStorage.setItem('refreshToken', response.data.data.refreshToken);
      await this.fetchUser();
    }
  };

  logout = async () => {
    await clientStore.treatyClient.api.auth.logout.post({
      refreshToken: localStorage.getItem('refreshToken') || '',
    });
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    this.user = null;
    setCrashUser();
    await clientStore.clearClients();
  };
}
